/** @format */
import { Context, Dispatch, SetStateAction, createContext } from 'react';

import { PromoCode } from '@toucan/client/global/application';
import { MixedTransaction } from '@toucan/client/global/transaction';

import {
    CalcServiceLineItem,
    CalcServiceMetadata,
    CalcServiceResponse,
    CalcServiceTaxItem,
    CalcServiceTotals,
    FormattedCalcServiceTotals, // eslint-disable-next-line boundaries/element-types -- I think this should be allowed, needs a discussion and re-visit
} from '@toucan/client/core/calculation-service';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface GetUpdatedTotalsReturn {
    hasValidPromoCode?: boolean;
}

export type FormattedTotals = FormattedCalcServiceTotals & {
    total: string;
    sellingTotal: string;
    buyingTotal: string;
    deliveryTotal: string;
    totalLabel: string;
};

export type CalculationContextTotals = CalcServiceTotals & {
    totalDelivery: number;
};

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CalculationContextProps {
    /**
     * The transaction
     */
    transaction?: MixedTransaction;

    /**
     * Promo code applied to the transaction
     */
    promoCode?: PromoCode;

    /**
     * Numbered totals.
     *
     * Worth noting these are not modified from the calc service. So a negative number
     * means the customer owes MPB.
     */
    totals?: CalculationContextTotals;

    /**
     * If the transaction is completed
     */
    isComplete: boolean;

    /**
     * If the totals are being fetched
     */
    isFetchingTotals: boolean;

    /**
     * The calculate endpoint fails avatax when the US shipping address
     * isn't correct. This isn't the right place to handle shipping
     * validation.
     */
    isValid: boolean;

    /**
     * If the balance is outbound i.e does mpb owe the customer
     */
    isOutbound: boolean;

    /**
     * Totals represented as strings with currency
     */
    formattedTotals?: FormattedTotals;

    /**
     * Line item array
     */
    lineItems: CalcServiceLineItem[];

    /**
     * Promo code setter
     */
    setPromoCode: Dispatch<SetStateAction<PromoCode | undefined>>;

    /**
     * Callback that gets the new calc service totals
     */
    getUpdatedTotals: (
        code?: string,
        isTemporaryCalculation?: boolean
    ) => Promise<GetUpdatedTotalsReturn>;

    /**
     * Function to update store with calculation response object
     */
    updateStore: (response: CalcServiceResponse) => void;

    /**
     * Function to update promo code
     */
    updatePromoCode: (promoCode: PromoCode | undefined) => void;

    /**
     * Tax information about the transaction
     */
    taxes: CalcServiceTaxItem[];

    /**
     * The meta data returned from the calc service
     */
    metadata?: CalcServiceMetadata;
}

export const CalculationContext: Context<CalculationContextProps> =
    createContext({} as CalculationContextProps);

CalculationContext.displayName = 'CalculationContext';
